import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/ui/Button';
import { BookOpen, BarChart3, Cloud, CheckCircle, TrendingUp, Target, Lock, Shield, Heart, ExternalLink, MessageCircle } from 'lucide-react';

const FeatureCard = ({ icon, title, description }) => (
  <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-8 text-center hover:shadow-lg transition-shadow">
    <div className="text-primary mb-4 flex justify-center">
      {icon}
    </div>
    <h3 className="text-xl font-semibold mb-3">{title}</h3>
    <p className="text-gray-600">{description}</p>
  </div>
);

export default function Home() {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [currentTagline, setCurrentTagline] = useState('Professional CEU Tracking for ASL Interpreters');

  const taglines = [
    'Professional CEU Tracking for ASL Interpreters',
    'Never Miss a Certification Deadline Again',
    'Your Professional Development, Organized',
    'Built by Interpreters, for Interpreters'
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTagline(prev => {
        const currentIndex = taglines.indexOf(prev);
        const nextIndex = (currentIndex + 1) % taglines.length;
        return taglines[nextIndex];
      });
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  const handleSignIn = async () => {
    try {
      navigate('/signin');
    } catch (error) {
      console.error('Navigation error:', error);
    }
  };

  const scrollToStory = () => {
    document.getElementById('story-section')?.scrollIntoView({ 
      behavior: 'smooth' 
    });
  };

  const handleSurveyClick = () => {
    window.open('https://docs.google.com/forms/d/e/1FAIpQLSffs0yIUCLLGDDyHR6zCEviKGS2M1V8TooQlK6_X-3w-0PjCw/viewform?usp=preview', '_blank');
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary to-secondary py-24 overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-7xl font-bold text-white mb-6 drop-shadow-lg">
              CertifiAble
            </h1>
            <p className="text-3xl text-white/95 mb-10 font-light">{currentTagline}</p>
            <div className="flex gap-4 justify-center">
              <Button 
                variant="outline" 
                size="lg" 
                onClick={handleSignIn}
                className="border-2 border-white text-white hover:bg-white/10"
              >
                Sign In / Register
              </Button>
              <Button 
                variant="outline" 
                size="lg" 
                onClick={scrollToStory}
                className="border-2 border-white text-white hover:bg-white/10"
              >
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="text-6xl mb-4">🔒</div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Your Data, Protected</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional-grade security protecting your credentials and personal information
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 rounded-xl p-8 text-center hover:shadow-lg transition-shadow">
              <Lock className="w-16 h-16 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3">Encrypted Storage</h3>
              <p className="text-gray-600">
                Your data is encrypted both when stored and when transmitted. Your certificates and personal information stay private and secure.
              </p>
            </div>

            <div className="bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 rounded-xl p-8 text-center hover:shadow-lg transition-shadow">
              <Shield className="w-16 h-16 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3">Access Controls</h3>
              <p className="text-gray-600">
                Only you can access your data. Proper authentication and authorization ensure your professional records remain yours alone.
              </p>
            </div>

            <div className="bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 rounded-xl p-8 text-center hover:shadow-lg transition-shadow">
              <Cloud className="w-16 h-16 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3">Secure Backups</h3>
              <p className="text-gray-600">
                Your certificates are safely backed up in secure cloud storage. No more lost paperwork or damaged files.
              </p>
            </div>
          </div>

          <p className="text-center mt-10 text-gray-500 italic text-lg">
            Built on AWS infrastructure with enterprise security practices
          </p>
        </div>
      </section>

      {/* Certification Support */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Built for RID & BEI Certification Maintenance
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Track your progress for Registry of Interpreters for the Deaf (RID) or Texas Board for Evaluation of Interpreters (BEI) certification requirements
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* RID */}
            <div className="bg-white border-2 border-primary rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <h3 className="text-3xl font-bold text-primary mb-6">RID Certification</h3>
              
              <div className="space-y-6">
                <div>
                  <div className="text-5xl font-bold text-primary mb-2">8.0 CEUs</div>
                  <p className="text-gray-600 text-lg">Total required every 4 years</p>
                </div>

                <div className="bg-primary/5 rounded-lg p-5">
                  <h4 className="font-semibold text-lg mb-3">Category Tracking:</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li>• 6.0 CEUs minimum in Professional Studies</li>
                    <li>• 1.0 CEU in Power, Privilege, & Oppression (PPO)</li>
                    <li>• Remaining hours in General Studies</li>
                  </ul>
                </div>

                <div className="space-y-2 text-gray-700">
                  <p>✓ Real-time progress tracking</p>
                  <p>✓ Category breakdown visualization</p>
                  <p>✓ PPO requirement monitoring</p>
                  <p>✓ 4-year cycle management</p>
                </div>
              </div>
            </div>

            {/* BEI */}
            <div className="bg-white border-2 border-secondary rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <h3 className="text-3xl font-bold text-secondary mb-6">BEI Certification (Texas)</h3>
              
              <div className="space-y-6">
                <div>
                  <div className="text-5xl font-bold text-secondary mb-2">10.0 CEUs</div>
                  <p className="text-gray-600 text-lg">Total required every 5 years</p>
                </div>

                <div className="bg-secondary/5 rounded-lg p-5">
                  <h4 className="font-semibold text-lg mb-3">Category Tracking:</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Interpreting, Ethics, General Studies</li>
                    <li>• Court & Medical (DHHS-approved)</li>
                    <li>• Trilingual specialization support</li>
                  </ul>
                </div>

                <div className="space-y-2 text-gray-700">
                  <p>✓ 5-year cycle tracking</p>
                  <p>✓ BEI-specific categories</p>
                  <p>✓ Certificate combination support</p>
                  <p>✓ DHHS compliance marking</p>
                </div>
              </div>
            </div>
          </div>

          <p className="text-center mt-10 text-gray-500 italic text-lg">
            Choose your primary certification to track
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to Track Like a Pro
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Designed specifically for ASL interpreters with RID and BEI certification requirements in mind.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard icon={<BookOpen />} title="Track Your CEUs" description="Easily log and manage your Continuing Education Units with detailed information about each workshop or training." />
            <FeatureCard icon={<BarChart3 />} title="Monitor Progress" description="Visual progress tracking toward your certification requirements with category breakdowns and analysis." />
            <FeatureCard icon={<Cloud />} title="Store Certificates" description="Upload and securely store your completion certificates for easy access and record keeping." />
            <FeatureCard icon={<CheckCircle />} title="Processing Status" description="Track when your certifying body has received and applied your CEUs to maintain accurate records." />
            <FeatureCard icon={<TrendingUp />} title="Persistent Records" description="Your data persists beyond your certification cycle for long-term tracking and historical records." />
            <FeatureCard icon={<Target />} title="Stay Organized" description="Never miss a deadline or requirement. Plan your professional development with confidence." />
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section id="story-section" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why CertifiAble Works
            </h2>
            <p className="text-xl text-gray-600">
              Built by an interpreter who's been exactly where you are. Here's what I learned about 
              CEU tracking the hard way—and how we can make it better for everyone.
            </p>
          </div>

          <div className="space-y-8 mb-12">
            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Let's Be Honest About CEU Tracking
              </h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                RID gives you access to your current transcript, but getting previous ones means submitting requests and waiting. Your certificates are scattered across email inboxes, computer folders, and maybe a few physical copies. Many interpreters rely solely on RID to track their CEUs, while others maintain spreadsheets or handwritten systems that work... until they don't.
              </p>
              <p className="text-primary font-semibold text-lg">
                Sound familiar? You're in good company.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                A Better Way Forward
              </h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                CertifiAble tracks your progress throughout your entire cycle, not just at the end. Store certificates digitally in one place where you can actually find them. Organize by categories and specialties so your expertise is clear and documented when you need to show qualifications for legal work, medical interpreting, or rate negotiations.
              </p>
              <p className="text-primary font-semibold text-lg">
                Professional organization that actually works with how you work.
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 rounded-xl p-8 mb-12 text-center">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              The Real Goal
            </h3>
            <p className="text-lg text-gray-700 leading-relaxed">
              Make CEU management so straightforward that you never have to think about it until 
              you need it. Stay organized without extra work. Be audit-ready without stress. 
              Focus on interpreting, not paperwork.
            </p>
          </div>

          <div className="text-center">
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              How This All Started
            </h3>
            <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
              <p>
                I thought I had my CEUs handled. My RID portal showed 8.2 total—looked good to me. 
                Turns out I completely missed the General Studies limit and was actually more than 
                4 CEUs short. That mistake cost me time, money, and a lot of unnecessary stress.
              </p>
              <p>
                Later, at a workshop, I overheard other interpreters struggling with the same tracking 
                issues. That's when I realized this wasn't just my problem—it's something our whole 
                community deals with.
              </p>
              <p className="text-primary font-semibold text-xl">
                So I built CertifiAble. For us. By us.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 bg-gradient-to-br from-primary to-secondary overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl font-bold text-white mb-6">
            Ready to Own Your Professional Path?
          </h2>
          <p className="text-2xl text-white/95 mb-4">
            Join ASL interpreters who track their CEUs like the professionals they are—organized, intentional, and unstoppable.
          </p>
          <p className="text-xl font-semibold text-white mb-10">
            Free during community testing phase • Help us build something amazing! 🚀
          </p>
          
          <div className="flex gap-4 justify-center">
            <Button 
              variant="outline" 
              size="lg" 
              onClick={handleSignIn}
              className="border-2 border-white text-white hover:bg-white/10"
            >
              Get Started Free
            </Button>
            <Button 
              variant="outline" 
              size="lg" 
              onClick={handleSurveyClick}
              className="border-2 border-white text-white hover:bg-white/10 inline-flex items-center gap-2"
            >
              <ExternalLink className="w-5 h-5" />
              Share Feedback
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
